import React, { useState, useEffect } from 'react';
import { Modal, Typography, Button, Loading, useToast, Alert as SpeculoAlert, AlertDescription } from '@crisellevaldez/speculo-ui';
import { useTenant } from '../../../../hooks/client/tenant/useTenant';
import { useOrganizationLogo } from '../../../../hooks/organizations/useOrganizationLogo';
import { useOrganizationFavicon } from '../../../../hooks/organizations/useOrganizationFavicon';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { Organization, PrimaryContact } from '../../../../types/organization.types';
import { useUpdateBranding } from '../../../../hooks/client/tenant/useUpdateBranding';
import { useClearCache } from '../../../../hooks/client/tenant/useClearCache';
import { brandingSchema, BrandingFormData } from '../../../../schemas/branding.schema';
import { Info, RotateCw } from 'lucide-react';
import { Tooltip } from '../../../common/Tooltip';
import { useUpdateOrganizationSettings } from '../../../../hooks/organizations/useUpdateOrganizationSettings';
import { tenantManagementApi } from '../../../../api/tenantManagement';
import { composeLinkUrl, decomposeLinkUrl, LinkScheme } from '../../../../utils/urlUtils';
import { ConfirmationDialog } from '../../../common/dialogs';
import { useUser } from '../../../../hooks/graphql/users/useUser';
import { AdminLockOverlay } from '../AdminLockOverlay';
import { usesThirdPartyIdx } from '../onboardingData/legacyIdx';
import { useOrganizationLegalSlug } from '../../../../hooks/graphql/organizations/useOrganizationLegalSlug';
import { isValidLegalSlug, proposeLegalSlug } from '../../../../utils/legalSlug';
import {
  GeneralSettingsSection,
  FooterSettingsSection,
  PropertyDetailsSection,
  SqueezePageSection,
  ThirdPartyIdxSection,
} from './sections';

interface UnifiedBrandingModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrg?: Organization;
  onSuccess?: () => void;
}

const LOGO_SIZES = {
  small: { width: 100, height: 50 },
  medium: { width: 200, height: 100 },
  large: { width: 260, height: 130 },
};

export const UnifiedBrandingModal: React.FC<UnifiedBrandingModalProps> = ({
  isOpen,
  onClose,
  selectedOrg,
  onSuccess,
}) => {
  // Query to get tenant data
  const { data: tenant, isLoading: isTenantLoading } = useTenant(selectedOrg?.id, isOpen);
  const { isAdmin, isSuperAdmin } = useUser();

  // Third-party IDX orgs get remi.homes legal pages for short links (SPEC-2782). The slug field
  // shows the saved slug (or one from the org name) until edited; a draft only applies to the org
  // it was typed for. Saving is offered whenever the value differs from what's saved.
  const thirdPartyIdx = usesThirdPartyIdx(selectedOrg?.onboardingData?.idx);
  const {
    legalSlug: savedLegalSlug,
    available: legalSlugAvailable,
    refetch: refetchLegalSlug,
  } = useOrganizationLegalSlug(selectedOrg?.id, isOpen && thirdPartyIdx);
  const [legalSlugDraft, setLegalSlugDraft] = useState<{ orgId: string; value: string } | null>(null);
  const legalSlugValue =
    legalSlugDraft && legalSlugDraft.orgId === selectedOrg?.id
      ? legalSlugDraft.value
      : (savedLegalSlug ?? proposeLegalSlug(selectedOrg?.org_name ?? ''));
  const legalSlugToSave =
    thirdPartyIdx && legalSlugAvailable && legalSlugValue.trim() !== (savedLegalSlug ?? '')
      ? legalSlugValue.trim()
      : '';

  const handleClose = () => {
    setLegalSlugDraft(null);
    onClose();
  };

  // Local state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFavicon, setSelectedFavicon] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  const [faviconUrl, setFaviconUrl] = useState('');
  const [logoSize, setLogoSize] = useState<'small' | 'medium' | 'large'>('small');
  const [logoPlacement, setLogoPlacement] = useState<'header' | 'both'>('header');
  const [newLinkTitle, setNewLinkTitle] = useState('');
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [newLinkScheme, setNewLinkScheme] = useState<LinkScheme>('https://');
  const [editingLinkKey, setEditingLinkKey] = useState<string | null>(null);
  const [orgLinks, setOrgLinks] = useState<{ key: string; value: string }[]>([]);
  const [showOptInConfirmation, setShowOptInConfirmation] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<BrandingFormData | null>(null);

  // Hooks
  const { showToast } = useToast();
  const { uploadLogo } = useOrganizationLogo();
  const { uploadFavicon } = useOrganizationFavicon();
  const { updateBranding } = useUpdateBranding();
  const { updateSettings: updateOrgSettings } = useUpdateOrganizationSettings();
  const { clearCache, isClearing: isClearingCache } = useClearCache();
  const queryClient = useQueryClient();

  // Form setup
  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    watch,
  } = useForm<BrandingFormData>({
    resolver: zodResolver(brandingSchema),
    defaultValues: {
      name: '',
      colors: { primary: '#000000', secondary: '#000000' },
      logo: { url: '' },
      complianceLinks: [],
      teamName: '',
      brokerageName: '',
      footer: { license: '' },
      propertyDetails: { contact: '', tour: '' },
      squeezePage: { language: '', consentStyle: undefined },
      primaryContact: {
        name: '',
        title: '',
        email: '',
        phone: '',
        useForPropertyPage: false,
      },
    },
  });

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setLogoUrl('');
      setFaviconUrl('');
      setLogoSize('small');
      setSelectedFile(null);
      setSelectedFavicon(null);
      return;
    }

    if (tenant?.branding) {
      if (tenant.branding.logo) {
        setLogoUrl(tenant.branding.logo.url || '');
        if (tenant.branding.logo.width === 200) {
          setLogoSize('medium');
        } else if (tenant.branding.logo.width === 260) {
          setLogoSize('large');
        } else {
          setLogoSize('small');
        }
        setLogoPlacement(tenant.branding.logo.placement === 'both' ? 'both' : 'header');
      } else {
        setLogoUrl('');
        setLogoSize('small');
        setLogoPlacement('header');
      }

      if (tenant.branding.favicon) {
        setFaviconUrl(tenant.branding.favicon.url || '');
      } else {
        setFaviconUrl('');
      }
    } else {
      setLogoUrl('');
      setFaviconUrl('');
      setLogoSize('small');
      setLogoPlacement('header');
    }
  }, [tenant, isOpen]);

  // Keep teamName in sync with name when "Show on home page" checkbox is on.
  // Without this, teamName goes stale if the user changes name after checking the box.
  const watchedName = watch('name');
  const watchedTeamName = watch('teamName');
  useEffect(() => {
    if (watchedTeamName && watchedTeamName !== watchedName) {
      setValue('teamName', watchedName || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedName]);

  // Update form when tenant or org data changes
  useEffect(() => {
    if (!tenant && selectedOrg) {
      setValue('name', selectedOrg.org_name || '');
      setValue('colors.primary', selectedOrg.branding?.primaryColor || '#000000');
      setValue('colors.secondary', selectedOrg.branding?.secondaryColor || '#000000');
      setValue('logo.url', selectedOrg.logoUrl || '');
      setLogoUrl(selectedOrg.logoUrl || '');
      setFaviconUrl(selectedOrg.faviconUrl || '');
      setValue('teamName', '');
      setValue('brokerageName', selectedOrg.brokerage_name || '');
      setValue('footer.license', '');
      setValue('propertyDetails.contact', '');
      setValue('propertyDetails.tour', '');
      setValue('squeezePage.language', '');
      setValue('squeezePage.consentStyle', undefined);
      return;
    }

    if (tenant) {
      setValue('name', tenant.name || '');
      setValue('colors.primary', tenant.branding?.colors?.primary || '#000000');
      setValue(
        'colors.secondary',
        tenant.branding?.colors?.secondary || tenant.branding?.colors?.primary || '#000000'
      );
      setValue('logo.url', tenant.branding?.logo?.url || '');
      setValue('teamName', tenant.branding?.teamName || '');
      setValue('brokerageName', tenant.branding?.brokerageName || selectedOrg?.brokerage_name || '');
      setValue('footer.license', tenant.branding?.footer?.license || '');
      setValue('propertyDetails.contact', tenant.branding?.propertyDetails?.contact || '');
      setValue('propertyDetails.tour', tenant.branding?.propertyDetails?.tour || '');
      setValue('squeezePage.language', tenant.branding?.squeezePage?.language || '');
      // idx_consent_style lives at the top level of the storefront config, not in
      // branding. An unset column stays undefined rather than being defaulted, so
      // saving an untouched modal cannot move a split-consent org onto the bundled
      // checkbox — undefined keeps it on the IDX site's allowlist fallback.
      setValue('squeezePage.consentStyle', tenant.idxConsentStyle);
    }
  }, [tenant, selectedOrg, setValue]);

  // Load initial org data
  useEffect(() => {
    if (!selectedOrg || !isOpen) return;

    const links = selectedOrg.org_links || {};
    const formattedLinks = Object.entries(links).map(([key, value]) => ({
      key,
      value: value as string,
    }));
    setOrgLinks(formattedLinks);
    setNewLinkTitle('');
    setNewLinkUrl('');
    setEditingLinkKey(null);

    const orgPrimaryContact = selectedOrg.primary_contact as PrimaryContact | undefined;
    if (orgPrimaryContact) {
      setValue('primaryContact', {
        name: orgPrimaryContact.name || '',
        title: orgPrimaryContact.title || '',
        email: orgPrimaryContact.email || '',
        phone: orgPrimaryContact.phone || '',
        useForPropertyPage: orgPrimaryContact.useForPropertyPage || orgPrimaryContact.use_for_property_page || false,
      });
    } else {
      setValue('primaryContact', {
        name: '',
        title: '',
        email: '',
        phone: '',
        useForPropertyPage: false,
      });
    }
  }, [selectedOrg, isOpen, setValue]);

  // Link handlers
  const handleAddLink = () => {
    if (!newLinkTitle || !newLinkUrl) return;

    // Link titles are the keys of the persisted org_links map, so they must
    // stay unique (a duplicate would silently overwrite the other link on save)
    const isDuplicateTitle = orgLinks.some(
      (link) => link.key === newLinkTitle && link.key !== editingLinkKey
    );
    if (isDuplicateTitle) {
      showToast({ message: 'A link with this title already exists', variant: 'error' });
      return;
    }

    const newLink = { key: newLinkTitle, value: composeLinkUrl(newLinkScheme, newLinkUrl) };
    if (editingLinkKey) {
      setOrgLinks((prevLinks) =>
        prevLinks.map((link) => (link.key === editingLinkKey ? newLink : link))
      );
    } else {
      setOrgLinks((prevLinks) => [...prevLinks, newLink]);
    }
    setEditingLinkKey(null);
    setNewLinkTitle('');
    setNewLinkUrl('');
  };

  const handleEditLink = React.useCallback(
    (linkKey: string) => {
      const link = orgLinks.find((l) => l.key === linkKey);
      if (!link) return;
      const { scheme, value } = decomposeLinkUrl(link.value);
      setEditingLinkKey(linkKey);
      setNewLinkTitle(link.key);
      setNewLinkScheme(scheme);
      setNewLinkUrl(value);
    },
    [orgLinks]
  );

  const handleDeleteLink = React.useCallback(
    (linkKey: string) => {
      if (!selectedOrg) return;
      setOrgLinks((prevLinks) => prevLinks.filter((link) => link.key !== linkKey));
      setEditingLinkKey((current) => (current === linkKey ? null : current));
    },
    [selectedOrg]
  );

  const handleResetLink = () => {
    setEditingLinkKey(null);
    setNewLinkTitle('');
    setNewLinkUrl('');
    setNewLinkScheme('https://');
  };

  // Handle opt-in confirmation
  const handleOptInConfirm = async () => {
    setShowOptInConfirmation(false);
    if (pendingFormData) {
      await performSave(pendingFormData);
      setPendingFormData(null);
    }
  };

  const handleOptInCancel = () => {
    setShowOptInConfirmation(false);
    setPendingFormData(null);
  };

  // Form submit handler with opt-in language confirmation
  const onSubmit = async (data: BrandingFormData) => {
    if (!selectedOrg) return;

    // Check if custom opt-in language is being set
    const hasCustomOptIn = data.squeezePage?.language && data.squeezePage.language.trim() !== '';
    const isChangingOptIn = hasCustomOptIn && data.squeezePage?.language !== tenant?.branding?.squeezePage?.language;

    // Show confirmation if setting/changing custom opt-in language
    if (isChangingOptIn) {
      setPendingFormData(data);
      setShowOptInConfirmation(true);
      return;
    }

    // Proceed with save
    await performSave(data);
  };

  // Actual save logic
  const saveLegalSlug = async () => {
    if (!selectedOrg || !legalSlugToSave) return;
    if (!isValidLegalSlug(legalSlugToSave)) {
      showToast({ message: 'Fix the legal pages slug before saving.', variant: 'error' });
      return;
    }
    try {
      setIsLoading(true);
      await updateOrgSettings({ id: selectedOrg.id, data: { legal_slug: legalSlugToSave } });
      setLegalSlugDraft(null);
      await refetchLegalSlug();
      showToast({ message: 'Legal pages slug saved', variant: 'success' });
      onSuccess?.();
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : 'Failed to save the legal pages slug',
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const performSave = async (data: BrandingFormData) => {
    if (!selectedOrg) return;

    try {
      setIsLoading(true);

      const brandingData = {
        colors: {
          primary: data.colors.primary,
          secondary: data.colors.secondary,
        },
        logo: {
          url: data.logo.url || tenant?.branding?.logo?.url || '',
          width: LOGO_SIZES[logoSize].width,
          height: LOGO_SIZES[logoSize].height,
          placement: logoPlacement,
        },
        favicon: tenant?.branding?.favicon || {},
        teamName: data.teamName || '',
        brokerageName: data.brokerageName || '',
        footer: {
          license: data.footer?.license || '',
        },
        propertyDetails: {
          contact: data.propertyDetails?.contact || '',
          tour: data.propertyDetails?.tour || '',
        },
        squeezePage: {
          language: data.squeezePage?.language || '',
        },
      };

      // Handle logo upload
      if (selectedFile) {
        try {
          const newLogoUrl = await uploadLogo(selectedFile, selectedOrg.id, logoUrl);
          brandingData.logo.url = newLogoUrl;
        } catch (uploadError) {
          console.error('Error uploading logo:', uploadError);
        }
      }

      // Handle favicon upload
      if (selectedFavicon) {
        try {
          const newFaviconUrl = await uploadFavicon(
            selectedFavicon,
            selectedOrg.id,
            tenant?.branding?.favicon?.url
          );
          if (!brandingData.favicon) {
            brandingData.favicon = {};
          }
          brandingData.favicon.url = newFaviconUrl;
        } catch (uploadError) {
          console.error('Error uploading favicon:', uploadError);
        }
      }

      // Update branding data and org links. idxConsentStyle lives in its own
      // Organizations column; only super admins see the Squeeze Page settings, so
      // anyone else saves without touching it (undefined leaves the column as is).
      await updateBranding(
        selectedOrg.id,
        brandingData,
        orgLinks,
        isSuperAdmin ? (data.squeezePage?.consentStyle ?? null) : undefined
      );

      // Update tenant name (Website/Team Name) if changed
      // Pass the FRESH brandingData to avoid overwriting it with stale tenant.branding
      if (data.name && data.name.trim() !== tenant?.name) {
        try {
          await tenantManagementApi.updateTenant(selectedOrg.id, {
            name: data.name.trim(),
            analytics: tenant?.analytics ?? { googleTag: '', gtmHead: '', gtmBody: '', gaHead: '' },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            branding: brandingData as any,
            skipRentals: tenant?.skipRentals ?? false,
          });
        } catch (tenantError) {
          console.error('Failed to update tenant name:', tenantError);
        }
      }

      // Update primary contact
      const primaryContactData: PrimaryContact = {
        name: data.primaryContact?.name || '',
        title: data.primaryContact?.title || '',
        email: data.primaryContact?.email || '',
        phone: data.primaryContact?.phone || '',
        useForPropertyPage: data.primaryContact?.useForPropertyPage || false,
      };

      await updateOrgSettings({
        id: selectedOrg.id,
        data: {
          primary_contact: primaryContactData,
          brokerage_name: data.brokerageName || '',
        },
      });

      // Invalidate queries
      await queryClient.invalidateQueries({ queryKey: ['tenant', selectedOrg.id] });
      await queryClient.invalidateQueries({ queryKey: ['organization', selectedOrg.id] });
      await queryClient.invalidateQueries({ queryKey: ['Organizations'] });

      // Auto-clear CDN cache for the tenant's domain
      if (tenant?.domain?.primary) {
        try {
          await clearCache(tenant.domain.primary);
        } catch (cacheError) {
          console.error('Failed to clear cache:', cacheError);
          // Don't fail the save if cache clear fails
        }
      }

      showToast({
        message: 'Branding settings updated successfully',
        variant: 'success',
      });
      onSuccess?.();
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : 'Failed to update branding',
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    // A third-party IDX org only has the legal pages slug, so the modal fits that one field.
    <Modal isOpen={isOpen} onClose={handleClose} size={thirdPartyIdx ? 'lg' : '2xl'}>
      <Modal.Header>
        <Typography variant="h3">Branding Settings</Typography>
      </Modal.Header>

      <Modal.Body>
        {isTenantLoading ? (
          <div className={thirdPartyIdx ? 'min-h-[160px]' : 'min-h-[600px]'}>
            <Loading />
          </div>
        ) : (
          <div className={thirdPartyIdx ? 'space-y-6' : 'space-y-6 min-h-[600px]'}>
            {thirdPartyIdx ? (
              // A third-party IDX org has no Speculo IDX site to brand; it only needs the
              // remi.homes legal pages used by short links.
              <AdminLockOverlay locked={!isAdmin} subject="Branding settings">
                {legalSlugAvailable && selectedOrg ? (
                  <ThirdPartyIdxSection
                    organizationId={selectedOrg.id}
                    savedSlug={savedLegalSlug}
                    value={legalSlugValue}
                    onChange={(value) => setLegalSlugDraft({ orgId: selectedOrg.id, value })}
                  />
                ) : (
                  <SpeculoAlert variant="info">
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      This org uses a third-party IDX, so the IDX site branding settings don&apos;t
                      apply. Its short-link legal pages setting isn&apos;t available on this
                      environment yet.
                    </AlertDescription>
                  </SpeculoAlert>
                )}
              </AdminLockOverlay>
            ) : (
              <>
                <AdminLockOverlay locked={!isAdmin} subject="Branding settings">
                  <GeneralSettingsSection
                    control={control}
                    register={register}
                    errors={errors}
                    watch={watch}
                    logoUrl={logoUrl}
                    faviconUrl={faviconUrl}
                    selectedFile={selectedFile}
                    selectedFavicon={selectedFavicon}
                    logoSize={logoSize}
                    logoPlacement={logoPlacement}
                    logoSizes={LOGO_SIZES}
                    onLogoSizeChange={setLogoSize}
                    onLogoPlacementChange={setLogoPlacement}
                    onFileSelect={setSelectedFile}
                    onFaviconSelect={setSelectedFavicon}
                  />

                  <FooterSettingsSection
                    register={register}
                    orgLinks={orgLinks}
                    newLinkTitle={newLinkTitle}
                    newLinkUrl={newLinkUrl}
                    newLinkScheme={newLinkScheme}
                    onNewLinkTitleChange={setNewLinkTitle}
                    onNewLinkUrlChange={setNewLinkUrl}
                    onNewLinkSchemeChange={setNewLinkScheme}
                    editingLinkKey={editingLinkKey}
                    onAddLink={handleAddLink}
                    onEditLink={handleEditLink}
                    onDeleteLink={handleDeleteLink}
                    onResetLink={handleResetLink}
                  />

                  <PropertyDetailsSection
                    control={control}
                    register={register}
                    errors={errors}
                    watch={watch}
                    selectedOrg={selectedOrg}
                  />

                  {/* Squeeze page consent and opt-in language are set by Speculo, not the client. */}
                  {isSuperAdmin && <SqueezePageSection control={control} />}
                </AdminLockOverlay>
              </>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="flex justify-between w-full">
          <div className={`flex items-center gap-2 ${thirdPartyIdx ? 'invisible' : ''}`}>
            <Button
              variant="outline"
              isLoading={isClearingCache}
              onClick={() => clearCache(tenant?.domain?.primary || '')}
              className="flex items-center gap-2 h-9"
            >
              <RotateCw className={`w-4 h-4 ${isClearingCache ? 'animate-spin' : ''}`} />
              Clear Cache
            </Button>
            <Tooltip
              content="Clear Cache removes cached branding data for your website. Use this after making changes to see updates immediately."
              position="top"
            >
              <Info className="h-4 w-4 text-gray-400 hover:text-primary cursor-help" />
            </Tooltip>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              isLoading={isLoading || isTenantLoading}
              onClick={thirdPartyIdx ? saveLegalSlug : handleSubmit(onSubmit)}
              disabled={!isAdmin || (thirdPartyIdx && !legalSlugToSave)}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </Modal.Footer>

      {/* Opt-in Language Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showOptInConfirmation}
        onClose={handleOptInCancel}
        onConfirm={handleOptInConfirm}
        title="Custom Opt-in Language Warning"
        message="Custom opt-in language must be compliant with your MLS provider's requirements. Using non-compliant language may result in violations or penalties. Do you want to proceed with this custom opt-in language?"
        confirmLabel="Yes, Save Custom Language"
        cancelLabel="Cancel"
        variant="warning"
        isLoading={isLoading}
      />
    </Modal>
  );
};

export default UnifiedBrandingModal;
